import { AccordionItemTitlePosition, CHZSendConfirmation3DImage, GiftIcon } from '@capsicum/common';
import { Accordion, Alert, Button, Divider, InfoTooltip, Text, useRouter } from '@capsicum/common-web';
import { ChevronDownIcon, InfoIcon } from '@capsicum/common-web/icons';
import {
	useWCPledgeActivityTab,
	useWCPledgeActivityTabTexts
} from '@capsicum/features/world-championship-pledge/shared';
import { ClaimPledgeReviewAtom } from '@capsicum/socios-core';
import { useSetAtom } from 'jotai';
import { useMemo } from 'react';
import styled, { useTheme } from 'styled-components';

import {
	WCPledgeListNoPledgesPlaceholderComponent,
	WCPledgeListTotalFooterItem,
	WCPledgeSummaryCard,
	WCPledgeUserPledgeItem,
	WCPledgeUserUnstakedPledgeItem
} from '../../ui/activityTab';

import { TabContentContainer } from './WCPledgeTabs.styled';

export const WCPledgeActivityTab = () => {
	const {
		cancelledPledges,
		claimablePledgeItemsForTxPayload,
		hasCancelledPledges,
		isLoading,
		totalAmounts,
		userPledges
	} = useWCPledgeActivityTab();
	const {
		alertText,
		cancelPledgeWarningModalTexts,
		noPledgesTexts,
		summarySectionTexts,
		yourChilizSectionTexts,
		yourPledgeSectionTexts
	} = useWCPledgeActivityTabTexts();
	const { colors } = useTheme();
	const setClaimReviewPayload = useSetAtom(ClaimPledgeReviewAtom);
	const router = useRouter();

	const handleConfirmClaim = () => {
		setClaimReviewPayload({
			chzAmount: totalAmounts.claimable.chz,
			claimablePledgeItems: claimablePledgeItemsForTxPayload
		});
		router.push('/locker-room-internationals/claim');
	};

	const unstakedPledges = useMemo(() => {
		return !cancelledPledges.unstaking.length ? (
			<WCPledgeListNoPledgesPlaceholderComponent
				testID="wcPledge-noUnstakingPledges"
				titleText={yourChilizSectionTexts.noUnstakingPledgesText}
			/>
		) : (
			<>
				{cancelledPledges.unstaking.map(unstakingPledge => (
					<WCPledgeUserUnstakedPledgeItem
						bonusAmount={unstakingPledge.bonus.chz}
						chzAmount={unstakingPledge.amount.toString()}
						isLoading={isLoading}
						key={unstakingPledge.team.name}
						teamFlag={unstakingPledge.team.flagUrl}
						teamName={unstakingPledge.team.name}
						teamStatus={unstakingPledge.team.status}
					/>
				))}

				<WCPledgeListTotalFooterItem
					chzAmount={totalAmounts.unstaking.chz}
					fiatAmount={totalAmounts.unstaking.fiat}
					testID="wcPledge-unstaking-pledges"
					titleText={yourChilizSectionTexts.totalText}
				/>
			</>
		);
	}, [
		cancelledPledges.unstaking,
		isLoading,
		yourChilizSectionTexts.noUnstakingPledgesText,
		totalAmounts.unstaking,
		yourChilizSectionTexts.totalText
	]);

	const claimablePledges = useMemo(() => {
		return !cancelledPledges.claimable.length ? (
			<WCPledgeListNoPledgesPlaceholderComponent
				testID="wcPledge-noClaimablePledges"
				titleText={yourChilizSectionTexts.noClaimablePledgesText}
			/>
		) : (
			<>
				{cancelledPledges.claimable.map(claimablePledge => (
					<WCPledgeUserUnstakedPledgeItem
						bonusAmount={claimablePledge.bonus.chz}
						chzAmount={claimablePledge.amount.toString()}
						isLoading={isLoading}
						key={claimablePledge.team.name}
						teamFlag={claimablePledge.team.flagUrl}
						teamName={claimablePledge.team.name}
						teamStatus={claimablePledge.team.status}
					/>
				))}

				<WCPledgeListTotalFooterItem
					chzAmount={totalAmounts.claimable.chz}
					fiatAmount={totalAmounts.claimable.fiat}
					testID="wcPledge-claimable-pledges"
					titleText={yourChilizSectionTexts.totalText}
				/>
				<S.StyledButton
					color="brand"
					fullWidth
					testID="wcpledge-claim-pledge-button"
					text={yourChilizSectionTexts.claimButtonText}
					onClick={() => {
						handleConfirmClaim();
					}}
				/>
			</>
		);
	}, [
		cancelledPledges.claimable,
		isLoading,
		yourChilizSectionTexts.noClaimablePledgesText,
		totalAmounts.claimable,
		yourChilizSectionTexts.totalText,
		yourChilizSectionTexts.claimButtonText
	]);

	const claimedPledges = useMemo(() => {
		return !cancelledPledges.claimed.length ? (
			<WCPledgeListNoPledgesPlaceholderComponent
				testID="wcPledge-noClaimedPledges"
				titleText={yourChilizSectionTexts.noClaimedPledgesText}
			/>
		) : (
			<>
				{cancelledPledges.claimed.map(claimedPledge => (
					<WCPledgeUserUnstakedPledgeItem
						bonusAmount={claimedPledge.bonus.chz}
						chzAmount={claimedPledge.amount.toString()}
						isLoading={isLoading}
						key={claimedPledge.team.name}
						teamFlag={claimedPledge.team.flagUrl}
						teamName={claimedPledge.team.name}
						teamStatus={claimedPledge.team.status}
					/>
				))}

				<WCPledgeListTotalFooterItem
					chzAmount={totalAmounts.claimed.chz}
					fiatAmount={totalAmounts.claimed.fiat}
					testID="wcPledge-claimed-pledges"
					titleText={yourChilizSectionTexts.totalText}
				/>
			</>
		);
	}, [
		cancelledPledges.claimed,
		isLoading,
		yourChilizSectionTexts.noClaimedPledgesText,
		totalAmounts.claimed,
		yourChilizSectionTexts.totalText
	]);

	const userPledgesList = useMemo(
		() =>
			userPledges.map(pledgeItem => (
				<>
					<Divider key={`${pledgeItem.team.name}-divider`} orientation="horizontal" />
					<WCPledgeUserPledgeItem
						cancelPledgeModalTexts={cancelPledgeWarningModalTexts}
						chzAmount={pledgeItem.amount.toString()}
						fiatAmount={pledgeItem.fiatAmount.toString()}
						ftName={pledgeItem.team.ftName}
						isLoading={isLoading}
						key={pledgeItem.team.name}
						stakeMoreButtonText={yourPledgeSectionTexts.stakeMore}
						teamFlagUrl={pledgeItem.team.flagUrl}
						teamId={pledgeItem.team.id}
						teamName={pledgeItem.team.name}
						teamStatus={pledgeItem.team.status}
						unstakeButtonText={yourPledgeSectionTexts.unstake}
					/>
				</>
			)),
		[userPledges, isLoading, cancelPledgeWarningModalTexts, yourPledgeSectionTexts]
	);

	if (!userPledges.length && !hasCancelledPledges && !isLoading) {
		return (
			<WCPledgeListNoPledgesPlaceholderComponent
				bodyText={noPledgesTexts.subHeading}
				testID="wcPledge-noPledges"
				titleText={noPledgesTexts.title}
			/>
		);
	}

	return (
		<S.StyledTabContentContainer>
			<S.ActivitySection>
				<Text variant="text9">{summarySectionTexts.title}</Text>
				<S.SummaryContainer>
					<WCPledgeSummaryCard
						chzAmount={totalAmounts.pledged.chz}
						fiatAmount={totalAmounts.pledged.fiat}
						image={CHZSendConfirmation3DImage}
						imageAlt="Chiliz transaction confirmed 3D asset"
						imageHeight={31}
						imageWidth={43}
						isLoading={isLoading}
						testID="pledged"
						title={summarySectionTexts.pledgedAmount}
					/>
					<WCPledgeSummaryCard
						chzAmount={totalAmounts.pledged.bonus.chz}
						fiatAmount={totalAmounts.pledged.bonus.fiat}
						image={GiftIcon}
						imageAlt="Gift icon"
						imageHeight={30}
						imageWidth={30}
						isLoading={isLoading}
						subtitle={summarySectionTexts.lastUpdated}
						testID="bonus"
						title={summarySectionTexts.chilizBonus}
					/>
				</S.SummaryContainer>
			</S.ActivitySection>
			<S.ActivitySection>
				<Text variant="text9">{yourPledgeSectionTexts.title}</Text>
				<S.PledgeListContainer>
					<S.PledgeListHeader>
						<Text variant="caption2">{yourPledgeSectionTexts.teamHeader}</Text>
						<Text variant="caption2">{yourPledgeSectionTexts.pledgedAmountHeader}</Text>
					</S.PledgeListHeader>
					{userPledgesList}
					<WCPledgeListTotalFooterItem
						chzAmount={totalAmounts.pledged.chz}
						fiatAmount={totalAmounts.pledged.fiat}
						testID="wcpledge-all-teams-list"
						titleText={yourPledgeSectionTexts.allTeamsFooter}
					/>
				</S.PledgeListContainer>
			</S.ActivitySection>
			{hasCancelledPledges && (
				<S.ActivitySection>
					<Text variant="text9">{yourChilizSectionTexts.title}</Text>
					<S.AccordionsWrapper>
						<Accordion
							items={[
								{
									condensed: true,
									content: <S.AccordionsContent>{unstakedPledges}</S.AccordionsContent>,
									icon: <ChevronDownIcon color={colors.icons.main.enabled} height={16} width={16} />,
									id: '1',
									testID: 'wcPledge-accordion-unstaking',
									title: (
										<S.TextTooltipWrapper variant="text1">
											{yourChilizSectionTexts.unstakingTitle}
											<InfoTooltip
												contentPosition="bottom_right"
												testID="wcPledge-unstaking-tooltip"
												zIndex={2}
											>
												{yourChilizSectionTexts.unstakeTipnoteText}
											</InfoTooltip>
										</S.TextTooltipWrapper>
									),
									titlePosition: AccordionItemTitlePosition.LEFT
								}
							]}
						/>
						<Divider orientation="horizontal" testID="wcpledge-activity-divider" />
						<Accordion
							items={[
								{
									condensed: true,
									content: <S.AccordionsContent>{claimablePledges}</S.AccordionsContent>,
									icon: <ChevronDownIcon color={colors.icons.main.enabled} height={16} width={16} />,
									id: '1',
									testID: 'wcPledge-accordion-claimable',
									title: <Text variant="text1">{yourChilizSectionTexts.claimableTitle}</Text>,
									titlePosition: AccordionItemTitlePosition.LEFT
								}
							]}
						/>
						<Divider orientation="horizontal" testID="wcpledge-activity-divider" />
						<Accordion
							items={[
								{
									condensed: true,
									content: <S.AccordionsContent>{claimedPledges}</S.AccordionsContent>,
									icon: <ChevronDownIcon color={colors.icons.main.enabled} height={16} width={16} />,
									id: '1',
									testID: 'wcPledge-accordion-claimed',
									title: <Text variant="text1">{yourChilizSectionTexts.claimedTitle}</Text>,
									titlePosition: AccordionItemTitlePosition.LEFT
								}
							]}
						/>
					</S.AccordionsWrapper>
				</S.ActivitySection>
			)}
			<Alert closable={false} icon={InfoIcon} type="message">
				<Text style={{ whiteSpace: 'pre-line' }} variant="text5">
					{alertText}
				</Text>
			</Alert>
		</S.StyledTabContentContainer>
	);
};

export namespace S {
	export const StyledTabContentContainer = styled(TabContentContainer)`
		gap: ${({ theme }) => theme.spacings.large};
		display: flex;
		flex-direction: column;
	`;

	export const ActivitySection = styled.div`
		display: flex;
		flex-direction: column;
		gap: ${({ theme }) => theme.spacings.small};
	`;

	export const AccordionsWrapper = styled.div`
		display: flex;
		flex-direction: column;
		background-color: ${({ theme }) => theme.colors.backgrounds.main.container};
		${({ theme }) => theme.border.radius.medium};
		padding: ${({ theme }) => theme.spacings.small};
		gap: ${({ theme }) => theme.spacings.medium};
	`;
	export const AccordionsContent = styled.div`
		margin-top: ${({ theme }) => theme.spacings.medium};
	`;

	export const SummaryContainer = styled.div`
		display: flex;
		flex-direction: row;
		gap: ${({ theme }) => theme.spacings.xxsmall};
	`;

	export const PledgeListContainer = styled.div`
		background-color: ${({ theme }) => theme.colors.backgrounds.main.container};
		${({ theme }) => theme.border.radius.medium};
		padding: ${({ theme }) => theme.spacings.small};
	`;

	export const PledgeListHeader = styled.div`
		display: flex;
		justify-content: space-between;
		align-items: center;
		padding: ${({ theme }) => theme.spacings.xxsmall} 0;
	`;
	export const StyledButton = styled(Button)`
		margin-top: ${({ theme }) => theme.spacings.xsmall};
	`;
	export const TextTooltipWrapper = styled(Text)`
		display: flex;
		align-items: center;
		gap: ${({ theme }) => theme.spacings.xxxsmall};
	`;
}
